// ===============================================================================
// BusinessGroups.js - Business Groups Dashboard for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/BusinessGroups.js
// Premium feature - Dashboard for managing business expense groups
// ===============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { businessAPI, formatCurrency, formatDate } from "./api";
import BusinessGroupDetail from "./BusinessGroupDetail";
import CreateBusinessGroup from "./CreateBusinessGroup";
import "./BusinessGroups.css";

function BusinessGroups({ user, onBack }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [limitStatus, setLimitStatus] = useState(null);
  
  // Navigation
  const [view, setView] = useState("list"); // list, detail, create
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  
  // Join Group Modal
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState("");
  
  // ===============================================================================
  // Load Data
  // ===============================================================================
  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [groupsRes, limitRes] = await Promise.all([
        businessAPI.listGroups(),
        businessAPI.getLimit()
      ]);
      
      if (groupsRes.data?.ok) {
        setGroups(groupsRes.data.groups || []);
      }
      if (limitRes.data?.ok) {
        setLimitStatus(limitRes.data);
      }
    } catch (err) {
      console.error("Load business groups error:", err);
      setError("Failed to load business groups");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // ===============================================================================
  // Navigation Handlers
  // ===============================================================================
  const handleViewGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setView("detail");
  };

  const handleCreateGroup = () => {
    if (limitStatus?.upgrade_required) {
      // Show upgrade modal
      return;
    }
    setView("create");
  };

  const handleGroupCreated = (group) => {
    loadGroups();
    setSelectedGroupId(group.id);
    setView("detail");
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedGroupId(null);
    loadGroups();
  };

  // ===============================================================================
  // Join Group Handler
  // ===============================================================================
  const handleJoinGroup = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      setJoinError("Please enter an invite code");
      return;
    }
    
    setJoinLoading(true);
    setJoinError("");
    
    try {
      const res = await businessAPI.joinGroup(inviteCode.trim());
      if (res.data?.ok) {
        setShowJoinModal(false);
        setInviteCode("");
        loadGroups();
        // Navigate to the joined group
        if (res.data.group?.id) {
          setSelectedGroupId(res.data.group.id);
          setView("detail");
        }
      }
    } catch (err) {
      console.error("Join group error:", err);
      setJoinError(err?.response?.data?.detail || "Failed to join group. Check the invite code.");
    }
    setJoinLoading(false);
  };

  // ===============================================================================
  // Render: Group Detail View
  // ===============================================================================
  if (view === "detail" && selectedGroupId) {
    return (
      <BusinessGroupDetail 
        groupId={selectedGroupId}
        user={user}
        onBack={handleBackToList}
        onGroupDeleted={handleBackToList}
      />
    );
  }

  // ===============================================================================
  // Render: Create Group View
  // ===============================================================================
  if (view === "create") {
    return (
      <CreateBusinessGroup 
        onBack={handleBackToList}
        onGroupCreated={handleGroupCreated}
      />
    );
  }

  // ===============================================================================
  // Render: Business Groups List (Main Dashboard)
  // ===============================================================================
  return (
    <div className="business-groups-page">
      {/* Header */}
      <div className="biz-header">
        <div className="biz-header-content">
          <h1>💼 Business Groups</h1>
          <p className="biz-subtitle">Track and split business expenses with your team</p>
        </div>
      </div>

      {/* Limit Status Banner */}
      {limitStatus && (
        <div className={`biz-limit-banner ${limitStatus.upgrade_required ? 'limit-reached' : ''}`}>
          <div className="limit-info">
            <span className="limit-count">
              {limitStatus.current_count} / {limitStatus.max_groups}
            </span>
            <span className="limit-text">Business Groups Used</span>
          </div>
          {limitStatus.upgrade_required && (
            <div className="upgrade-prompt">
              <p>Need more business groups?</p>
              <a 
                href={limitStatus.upgrade_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="upgrade-link"
              >
                Upgrade to Split for Business →
              </a>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && <div className="biz-error">{error}</div>}

      {/* Loading State */}
      {loading ? (
        <div className="biz-loading">
          <div className="biz-spinner"></div>
          <p>Loading business groups...</p>
        </div>
      ) : (
        <>
          {/* Action Buttons */}
          <div className="biz-action-buttons">
            {!limitStatus?.upgrade_required && (
              <button className="biz-create-btn" onClick={handleCreateGroup}>
                <span className="create-icon">+</span>
                <span>Create Business Group</span>
              </button>
            )}
            {!limitStatus?.upgrade_required && (
            <button className="biz-join-btn" onClick={() => setShowJoinModal(true)}>
               <span className="join-icon">🔗</span>
               <span>Join with Invite Code</span>
            </button>
           )}
          </div>

          {/* Groups Grid */}
          {groups.length === 0 ? (
            <div className="biz-empty-state">
              <div className="empty-icon">💼</div>
              <h3>No Business Groups Yet</h3>
              <p>Create your first business group or join one with an invite code</p>
              <div className="biz-empty-actions">
                {!limitStatus?.upgrade_required && (
                  <button className="biz-create-btn-small" onClick={handleCreateGroup}>
                    Create Business Group
                  </button>
                )}
                {!limitStatus?.upgrade_required && (
                 <button className="biz-join-btn-small" onClick={() => setShowJoinModal(true)}>
                  Join with Code
                 </button>
                )}
              </div>
            </div>
          ) : (
            <div className="biz-groups-grid">
              {groups.map(group => (
                <div 
                  key={group.id} 
                  className="biz-group-card"
                  onClick={() => handleViewGroup(group.id)}
                >
                  <div className="biz-card-header">
                    <span className="biz-type-icon">{group.type_icon}</span>
                    <span className="biz-type-name">{group.type_name}</span>
                  </div>
                  <h3 className="biz-group-name">{group.name}</h3>
                  {group.description && (
                    <p className="biz-group-desc">{group.description}</p>
                  )}
                  <div className="biz-card-footer">
                    <span className="biz-members">
                      👥 {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                    </span>
                    <span className="biz-currency">{group.currency}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upgrade CTA for limit reached */}
          {limitStatus?.upgrade_required && (
            <div className="biz-upgrade-cta">
              <div className="upgrade-cta-content">
                <h3>🚀 Need More Business Groups?</h3>
                <p>
                  Split for Business offers unlimited business groups, advanced analytics, 
                  team management, and priority support for growing businesses.
                </p>
                <div className="upgrade-features">
                  <div className="feature">✓ Unlimited Groups</div>
                  <div className="feature">✓ Advanced Reports</div>
                  <div className="feature">✓ Team Management</div>
                  <div className="feature">✓ API Access</div>
                </div>
                <a 
                  href={limitStatus.upgrade_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="upgrade-cta-btn"
                >
                  Explore Split for Business
                </a>
              </div>
              <div className="upgrade-cta-image">
                <div className="cta-icon">💼</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Join Group Modal */}
      {showJoinModal && (
        <div className="biz-modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="biz-modal" onClick={e => e.stopPropagation()}>
            <div className="biz-modal-header">
              <h2>🔗 Join Business Group</h2>
              <button className="biz-modal-close" onClick={() => setShowJoinModal(false)}>×</button>
            </div>
            <form onSubmit={handleJoinGroup} className="biz-modal-form">
              <div className="biz-form-row">
                <label>Invite Code</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Enter invite code (e.g., BIZACMEX1234ABCD)"
                  disabled={joinLoading}
                  autoFocus
                />
                <p className="biz-form-help">
                  Ask the group admin for the invite code
                </p>
              </div>
              {joinError && <div className="biz-form-error">{joinError}</div>}
              <div className="biz-modal-actions">
                <button 
                  type="button" 
                  className="biz-cancel-btn"
                  onClick={() => setShowJoinModal(false)}
                  disabled={joinLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="biz-submit-btn"
                  disabled={joinLoading || !inviteCode.trim()}
                >
                  {joinLoading ? 'Joining...' : 'Join Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessGroups;

